<template>
  <div>
    <div class="flex flex-wrap lg:mr-[-1.0714rem] px-[1.7143rem] gap-[0.8571rem] lg:gap-0 lg:px-0 -mx-3 lg:mx-0">
        <div
          v-for="(item, index) in displayItems"
          :key="`${item.type}-${item.id ?? index}`"
          :class="isVisibleDisplayItem(item) ? 'product-tile lg:p-3 w-[48%] lg:w-1/3' : 'hidden'"
        >
          <TaiaCard v-if="item.type == 'taia' && item.ad && !hideTaiaAndQuizCard" :selectedAd="item.ad" class="marketing-product" />
          <ProductCard v-else-if="item.type == 'product'" v-bind="item" :indexValue="item.id ?? index" :id="item.id"
            :favoriteIds="favoriteIds" @toggle-favorite="toggleFavorite" :pricing="{
              hasMultiplePrices: item?.hasMultiplePrices,
              priceRange: item?.priceRange,
              retailPrice: item?.retailPrice,
              price: item?.price,
              wholesalePrice: item?.wholesalePrice,
              loyaltyPointCost: item?.loyaltyPointCost
            }" />
        </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { storeToRefs } from 'pinia'

import { CONTENT_TYPES, ENTITY_TYPE } from '~~/shared/utils/contentTypes'
import type { MarketingAd, ProductItem, PlpProduct } from '~~/shared/types/common'
import { useIframeStore } from '~/stores/chatAIStore'
import { useLocaleStore } from '~/stores/localeStore'

import useSiteService from '~/composables/siteService'
import { useMarketing } from '~/composables/useMarketing'
import { useProducts } from '~/composables/useProducts'

import ProductCard from '../organism/product/productTile.vue'
import TaiaCard from '../organism/product/taiaCard.vue'


const props = defineProps<{
  productData: PlpProduct[] | null,
  hideTaiaCard?: boolean
}>()

const { getFavorites, unfavoriteProduct, favoriteProduct } = useSiteService()
const favoriteIds = ref<number[]>([])

onMounted(async () => {
  try {
    const productsFav: any = await getFavorites(CONTENT_TYPES.PRODUCT)
    favoriteIds.value = Array.isArray(productsFav) ? productsFav : []
  } catch {
    favoriteIds.value = []
  }
})

const toggleFavorite = async (id: number) => {
  if (favoriteIds.value.includes(id)) {
    favoriteIds.value = favoriteIds.value.filter(i => i !== id)
    await unfavoriteProduct(id)
  } else {
    favoriteIds.value.push(id)
    await favoriteProduct(id)
  }
}

const productsNode = computed<any>(() =>
  (props.productData as any)?.page?.products ??
  (props.productData as any)?.products ??
  props.productData ?? []
)

const rawProducts = computed<any[]>(() => productsNode.value?.data ?? productsNode.value ?? [])

const formatBenefits = (benefits: string[]) => {
  if (!benefits?.length) return ''
  if (benefits.length <= 2) return benefits.join(', ')
  return `${benefits.slice(0, 2).join(', ')} +${benefits.length - 2} more`
}

const resolveProductBadge = (badges: string[] = [], customBadges: string[] = []) => {
  if (customBadges.length) return customBadges[0]
  const normalized = badges.map(b => b.toLowerCase())
  if (normalized.includes('new')) return badges[normalized.indexOf('new')]
  if (normalized.includes('exclusive')) return badges[normalized.indexOf('exclusive')]
  if (normalized.includes('Out of Stock')) return badges[normalized.indexOf('Out of Stock')]
  if (normalized.includes('discontinued')) return badges[normalized.indexOf('To Be Discontinued')]
  return badges[0]
}

const INVALID_PRODUCT_URLS = new Set(['undefined', '/products/dp/undefined'])

const logInvalidProductUrl = (product: any, index: number) => {
  console.error('[product list] invalid product url', {
    productIsUndefined: product === undefined,
    index,
    productId: product?.id,
    productSku: product?.sku,
    productName: product?.name ?? product?.productName ?? product?.individualProductName,
    productUrl: product?.url,
  })
}

const resolveProductUrl = (product: any, index: number) => {
  if (!product) {
    logInvalidProductUrl(product, index)
    return null
  }

  const url = product.url
  if (url === undefined || url === null || INVALID_PRODUCT_URLS.has(String(url))) {
    logInvalidProductUrl(product, index)
    return null
  }

  return String(url)
}

const ProductsList = computed<ProductItem[]>(() =>
  rawProducts.value.flatMap((product: any, index: number) => {
    const url = resolveProductUrl(product, index)
    if (!url) return []

    return [{
      id: product.id,
      sku: product.sku,
      name: product.name ?? product.productName ?? product.individualProductName,
      url,
      price: product.salePrice,
      retailPrice : product.retailPrice,
      wholesalePrice : product.wholesalePrice,
      hasMultiplePrices: product.hasMultiplePrices,
      loyaltyPointCost: product.loyaltyPointCost,
      primaryImageUrl: product.primaryImageUrl ?? '',
      benefits: formatBenefits(product.benefits ?? []),
      headline: product.headline ?? '',
      shortDescription: product.shortDescription ?? '',
      reviewSummary: {
        reviewCount: product.reviewCount ?? 0,
        averageRating: product.averageRating ?? 0
      },
      tag: resolveProductBadge(
        product.badges?.map((b: any) => b.name ?? b) ?? [],
        product.customBadges ?? []
      ),
      type: 'product'
    }]
  })
)

const iframeStore = useIframeStore()
const { isEnableTaia } = storeToRefs(iframeStore)
const { hideTaiaAndQuizCard } = useProducts()

const { data, refresh } = useMarketing()

const localeStore = useLocaleStore();
const countryCode = computed(() => {
  const code = (localeStore.locale as any)?.country?.code
  return typeof code === 'string' && code.trim() ? code : 'US'
})

type ProductGridItem = ProductItem | {
  id: string
  type: 'taia'
  ad: MarketingAd | null
}

const isVisibleDisplayItem = (item: ProductGridItem) =>
  item.type === 'product' || (!hideTaiaAndQuizCard.value && Boolean(item.ad))

const selectedAd = computed<MarketingAd | null>(() => {
  const ads = data.value as MarketingAd[] | undefined
  if (!ads?.length) return null

  const eligibleAds = ads.filter(ad => {
    const whitelist = ad.data.countryWhitelist
    const blacklist = ad.data.countryBlacklist

    const whitelisted =
      !whitelist?.length || whitelist.some(c => c.countryCode === countryCode.value)

    const notBlacklisted =
      !blacklist?.length || !blacklist.some(c => c.countryCode === countryCode.value)

    return whitelisted && notBlacklisted
  })

  const taia = eligibleAds.find(ad => ad.data.entityType === ENTITY_TYPE.TAIA)
  if (isEnableTaia.value &&  taia) return taia

  return null
})

const displayItems = computed<ProductGridItem[]>(() => {
  const items: ProductGridItem[] = [...ProductsList.value]

  if (ProductsList.value.length >= 2 && !props.hideTaiaCard) {
    items.splice(2, 0, { id: 'primary-marketing-slot', type: 'taia', ad: selectedAd.value })
  }

  return items
})

onMounted(() => {
  refresh()
})
</script>
